import { useState, type CSSProperties, type ReactNode } from "react";
import {
  Box,
  ButtonBase,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { reorderIds } from "@shared/catalogue-order";
import { tokens } from "../../theme";

/**
 * One entry of a global catalogue, reduced to what a per-premise picker needs.
 * Apps carry a brand fallback (`color`/`wordmark`/`letter`) for the tile when no
 * logo has been uploaded; radio channels only ever have a logo.
 *
 * NB: the entry's `text` field is deliberately NOT read here. It is ink for the
 * brand-coloured tile the TV draws; this tile is white, so it would usually be
 * white-on-white. See `LogoTile`.
 */
export interface CatalogueItem {
  id: string;
  name: string;
  tag?: string;
  logoUrl?: string | null;
  color?: string;
  wordmark?: string;
  letter?: string;
}

interface Props {
  items: CatalogueItem[];
  isOn: (id: string) => boolean;
  onToggle: (id: string) => void;
  /** Singular noun for the aria-label, e.g. "app" / "channel". */
  noun: string;
  /** Enables reordering. Called with the FULL id list in its new order - the
   *  caller stores that verbatim, so a later catalogue change can't unpick the
   *  operator's arrangement. Omit for a picker with a fixed order. */
  onReorder?: (ids: string[]) => void;
  /**
   * Ids whose POSITION is not the operator's, mapped to the reason.
   *
   * A pinned tile sits at the head and carries no drag grip, while everything
   * around it stays reorderable - the pin costs exactly the one decision a
   * vendor agreement takes away and no others. Its SWITCH stays live: Netflix
   * asks to come first where it is carried, not that a hotel carry it, so
   * switching the app off is still entirely the operator's call and is what
   * lifts the pin. See `@shared/app-certification`.
   */
  pinnedBy?: ReadonlyMap<string, string>;
  "data-testid"?: string;
}

/**
 * A per-premise on/off picker rendered as a grid of brand tiles, in the order
 * the guest will see them.
 *
 * These lists used to be a column of switches with the app's name in 13px text
 * beside them - an operator picking "the streaming apps guests expect" was
 * reading a spreadsheet of words for something they recognise instantly by
 * logo. The grid shows what the guest will see on the TV, at the size the
 * decision actually needs.
 *
 * The whole card is ONE control (`role="switch"`): the visible Switch is
 * decorative and inert, so a card is one click target rather than a tile with a
 * separate small hit area in the corner.
 *
 * When `onReorder` is given the tile is also draggable, by two paths on purpose:
 * press and drag anywhere on the tile (what an operator tries first), or the
 * grip in its corner, which is the KEYBOARD path - focus it, Space to lift,
 * arrows to move, Space to drop. The grip is why the card can't simply carry the
 * drag listeners itself: Space on the card already means "toggle", and the order
 * apps appear in on a TV cannot be a mouse-only decision.
 */
export default function CatalogueGrid({
  items,
  isOn,
  onToggle,
  noun,
  onReorder,
  pinnedBy,
  "data-testid": testId,
}: Props) {
  const pinReason = (id: string) => pinnedBy?.get(id);
  const [dragging, setDragging] = useState<string | null>(null);
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so the tile stays an
    // ordinary click target for the toggle it also is.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Only the movable tiles are sortable ids. A pinned one registers no
  // sortable, so listing it here would leave dnd-kit trying to make room for a
  // node that never moves.
  const ids = items.filter((i) => !pinReason(i.id)).map((i) => i.id);

  const grid = (
    <Box
      data-testid={testId}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 1.5,
      }}
    >
      {items.map((item) =>
        onReorder && !pinReason(item.id) ? (
          <SortableCatalogueCard
            key={item.id}
            item={item}
            on={isOn(item.id)}
            noun={noun}
            onToggle={() => onToggle(item.id)}
          />
        ) : (
          <CatalogueCard
            key={item.id}
            item={item}
            on={isOn(item.id)}
            noun={noun}
            onToggle={() => onToggle(item.id)}
            pinned={pinReason(item.id)}
          />
        )
      )}
    </Box>
  );

  if (!onReorder) return grid;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(null);
    if (!over) return;
    // The whole list is written out, not just the moved id: an explicit order
    // must survive the catalogue growing under it.
    const next = reorderIds(ids, String(active.id), String(over.id));
    // The stored order must still name every tile, pinned ones included, or the
    // next catalogue change would drop them to the end of the list the moment
    // the pin lifts. Pinned ids keep the head, which is where the rule that
    // pinned them puts them anyway.
    if (next)
      onReorder([
        ...items.map((i) => i.id).filter((id) => !next.includes(id)),
        ...next,
      ]);
  };

  const activeItem = dragging
    ? items.find((i) => i.id === dragging)
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }: DragStartEvent) =>
        setDragging(String(active.id))
      }
      onDragCancel={() => setDragging(null)}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        {grid}
      </SortableContext>
      {/* The tile follows the cursor at full size - a ghost outline would not
          say which of a dozen near-identical logos is in flight. */}
      <DragOverlay>
        {activeItem ? (
          <CatalogueCard
            item={activeItem}
            on={isOn(activeItem.id)}
            noun={noun}
            onToggle={() => {}}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * The tile as it lives in the grid. Kept separate from {@link CatalogueCard} so
 * the DragOverlay copy - which renders OUTSIDE the SortableContext - never calls
 * `useSortable` and never registers a second draggable under the same id.
 */
function SortableCatalogueCard(props: {
  item: CatalogueItem;
  on: boolean;
  noun: string;
  onToggle: () => void;
}) {
  const sortable = useSortable({ id: props.item.id });
  // dnd-kit types its listener map as `Record<string, Function>`; narrow the one
  // we lift out of it rather than casting the whole prop away.
  const onPointerDown = sortable.listeners?.onPointerDown as
    | ((e: React.PointerEvent) => void)
    | undefined;
  return (
    <CatalogueCard
      {...props}
      setNodeRef={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        // The original stays put as a hole while its overlay flies, so the grid
        // never re-flows under the cursor mid-drag.
        opacity: sortable.isDragging ? 0.35 : undefined,
      }}
      // Pointer drag from anywhere on the tile. Only the POINTER activator goes
      // on the card - dnd-kit's keyboard activator is `onKeyDown`, and Space on
      // this card already means "toggle". The grip below carries both.
      onPointerDown={onPointerDown}
      grip={{ ...sortable.attributes, ...sortable.listeners }}
    />
  );
}

function CatalogueCard({
  item,
  on,
  noun,
  onToggle,
  setNodeRef,
  style,
  onPointerDown,
  grip,
  pinned,
}: {
  item: CatalogueItem;
  on: boolean;
  noun: string;
  onToggle: () => void;
  setNodeRef?: (el: HTMLElement | null) => void;
  style?: CSSProperties;
  onPointerDown?: (e: React.PointerEvent) => void;
  /** dnd-kit attributes + listeners for the corner handle. */
  grip?: Record<string, unknown>;
  /** Why this tile's POSITION is fixed. Rendered under the name, and the
   *  reason the grip is withheld. The switch is unaffected - a pin is not a
   *  lock, and switching the app off is what lifts it. */
  pinned?: string;
}) {
  const card = (
    <ButtonBase
      role="switch"
      aria-checked={on}
      aria-label={`${item.name} ${noun}`}
      onClick={onToggle}
      onPointerDown={onPointerDown}
      data-testid={`catalogue-card-${item.id}`}
      sx={{
        display: "block",
        textAlign: "left",
        width: "100%",
        bgcolor: tokens.surface,
        border: "1px solid",
        borderColor: on ? tokens.brand : tokens.divider,
        borderRadius: "14px",
        boxShadow: tokens.shadowCard,
        overflow: "hidden",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": { borderColor: on ? tokens.brand : tokens.inkFaint },
      }}
    >
      <LogoTile item={item} on={on} />
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ pl: 1.5, pr: 0.5, py: 0.75 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: on ? tokens.ink : tokens.inkFaint,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.name}
          </Typography>
          {(pinned || item.tag) && (
            <Typography
              sx={{
                fontSize: 11,
                // The pin reason is the more useful of the two when both exist:
                // the tag says what kind of app this is, which the logo above
                // already said, and the reason answers the question the
                // operator is about to ask.
                color: pinned ? tokens.brand : tokens.inkFaint,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {pinned || item.tag}
            </Typography>
          )}
        </Box>
        {/* Decorative: the card itself is the switch (see role above). */}
        <Switch
          size="small"
          checked={on}
          tabIndex={-1}
          inputProps={{ "aria-hidden": true, tabIndex: -1 }}
          sx={{ pointerEvents: "none", flex: "none" }}
        />
      </Stack>
    </ButtonBase>
  );

  if (!grip || pinned)
    return (
      <Wrapper setNodeRef={setNodeRef} style={style}>
        {card}
      </Wrapper>
    );

  return (
    <Wrapper setNodeRef={setNodeRef} style={style}>
      {card}
      {/* Sits OUTSIDE the ButtonBase: a <button> inside a <button> is invalid
          markup and screen readers skip the inner one. */}
      <Tooltip title="Drag to reorder">
        <IconButton
          size="small"
          aria-label={`Reorder ${item.name}`}
          data-testid={`catalogue-grip-${item.id}`}
          {...grip}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            // The tile behind it is always white, in both themes - so this ink
            // is a fixed hex, never a token that flips with the mode.
            color: TILE_INK,
            opacity: 0.35,
            cursor: "grab",
            transition: "opacity 120ms ease",
            "&:hover, &:focus-visible": { opacity: 1 },
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Wrapper>
  );
}

/** Positioning context for the corner grip, and the sortable's node ref. */
function Wrapper({
  setNodeRef,
  style,
  children,
}: {
  setNodeRef?: (el: HTMLElement | null) => void;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: "relative",
        "&:hover .MuiIconButton-root": { opacity: 1 },
      }}
    >
      {children}
    </Box>
  );
}

/** The tile is always white, in both themes - so its ink must be fixed too,
 *  never a theme token that flips light in dark mode. */
const TILE_BG = "#FFFFFF";
const TILE_INK = "#0F172A";

/**
 * Relative luminance of a #rgb / #rrggbb colour, or null if it isn't one.
 * Used to reject a brand colour too pale to read as ink on the white tile.
 */
export function luminance(hex: string): number | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h =
    m[1].length === 3
      ? m[1]
          .split("")
          .map((c) => c + c)
          .join("")
      : m[1];
  const chan = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const [r, g, b] = chan.map(lin);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * The logo tile - always a white chip, in both themes.
 *
 * Brand-coloured tiles made the grid a patchwork of competing blocks, and every
 * uploaded logo is drawn for a light background anyway, so half of them were
 * fighting the colour behind them. White is the surface the artwork expects.
 *
 * Order of preference matches what the TV itself renders: uploaded logo →
 * wordmark → single letter. The two text fallbacks can no longer use the
 * entry's `text` colour (that is on-brand-colour ink, usually white, which
 * would vanish here) - they take the brand colour itself as ink, unless it is
 * too pale to read.
 */
function LogoTile({ item, on }: { item: CatalogueItem; on: boolean }) {
  const lum = item.color ? luminance(item.color) : null;
  const fg = item.color && lum !== null && lum < 0.7 ? item.color : TILE_INK;
  return (
    <Box
      data-testid="logo-tile"
      sx={{
        aspectRatio: "16 / 9",
        bgcolor: TILE_BG,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: "1px solid",
        borderColor: tokens.divider,
      }}
    >
      {item.logoUrl ? (
        <Box
          component="img"
          src={item.logoUrl}
          alt=""
          sx={{
            maxWidth: "72%",
            maxHeight: "68%",
            objectFit: "contain",
            display: "block",
            // Dim the artwork, never the tile: a translucent white chip would
            // just bleed into the card behind it. Off must still read as a
            // white tile holding a greyed logo.
            filter: on ? "none" : "grayscale(1)",
            opacity: on ? 1 : 0.4,
            transition: "opacity 120ms ease, filter 120ms ease",
          }}
        />
      ) : (
        <Typography
          sx={{
            color: on ? fg : TILE_INK,
            filter: on ? "none" : "grayscale(1)",
            opacity: on ? 1 : 0.4,
            transition: "opacity 120ms ease, filter 120ms ease",
            fontWeight: 800,
            letterSpacing: item.wordmark ? ".02em" : 0,
            fontSize: item.wordmark ? 15 : 26,
            px: 1,
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.wordmark || item.letter || item.name.charAt(0).toUpperCase()}
        </Typography>
      )}
    </Box>
  );
}
